import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Phone,
    Copy,
    MapPin,
    Clock,
    Stethoscope,
    Search,
    Filter,
    CheckCircle,
    Video
} from 'lucide-react';
import { emergencyContacts, hospitals, pharmacies, doctors } from '../data/mockData';
import MockPayment from '../components/MockPayment';
import LiveMap from '../components/LiveMap';

const EmergencyView = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const [activeTab, setActiveTab] = useState(queryParams.get('tab') || 'contacts');
    const [showPayment, setShowPayment] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const handleConsultation = (doctor) => {
        setSelectedDoctor(doctor);
        setShowPayment(true);
    };

    const copyToClipboard = (num) => {
        navigator.clipboard.writeText(num);
        alert('Number copied: ' + num);
    };

    // Helper to roughly get coordinates based on area
    const getCoords = (area) => {
        if (area.includes('Victoria Island')) return [6.4281, 3.4219];
        if (area.includes('Lagos Island')) return [6.4541, 3.4064];
        if (area.includes('Idi-Araba')) return [6.5204, 3.3512];
        if (area.includes('Ikeja')) return [6.6018 + (Math.random() * 0.01), 3.3515 + (Math.random() * 0.01)]; // add jitter for multiple
        if (area.includes('Lekki')) return [6.4698 + (Math.random() * 0.01), 3.5852 + (Math.random() * 0.01)];
        if (area.includes('Ikoyi')) return [6.4500 + (Math.random() * 0.01), 3.4333 + (Math.random() * 0.01)];
        if (area.includes('Yaba')) return [6.5095, 3.3711];
        return [6.5244, 3.3792]; // default Lagos
    };

    const mapMarkers = hospitals.map(h => ({
        position: getCoords(h.area),
        title: h.name,
        description: h.area + ' • ' + (h.is24hr ? '24hr' : '')
    }));

    return (
        <div className="emergency-view animate-fade">
            <div className="tab-navigation">
                <button className={activeTab === 'contacts' ? 'active' : ''} onClick={() => setActiveTab('contacts')}>Contacts</button>
                <button className={activeTab === 'hospitals' ? 'active' : ''} onClick={() => setActiveTab('hospitals')}>Hospitals</button>
                <button className={activeTab === 'pharmacies' ? 'active' : ''} onClick={() => setActiveTab('pharmacies')}>Pharmacies</button>
                <button className={activeTab === 'telemedicine' ? 'active' : ''} onClick={() => setActiveTab('telemedicine')}>Consult</button>
            </div>

            <div className="tab-content">
                {activeTab === 'contacts' && (
                    <div className="contacts-list">
                        <div className="emergency-header">
                            <h2>Lagos Emergency Hotline</h2>
                            <div className="main-number card">
                                <span className="number">112 / 767</span>
                                <p>24/7 Response Center</p>
                                <div className="actions">
                                    <button className="primary-btn"><Phone size={18} /> Call Now</button>
                                </div>
                            </div>
                        </div>

                        {emergencyContacts.map(contact => (
                            <div key={contact.id} className="contact-item card">
                                <div className="info">
                                    <h4>{contact.name}</h4>
                                    <span className="num">{contact.number}</span>
                                </div>
                                <div className="btns">
                                    <button onClick={() => copyToClipboard(contact.number)} className="icon-btn"><Copy size={18} /></button>
                                    <button className="icon-btn call"><Phone size={18} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === 'hospitals' && (
                    <div className="hospitals-view">
                        <div className="map-mock card" style={{ padding: 0, overflow: 'hidden' }}>
                            <LiveMap 
                                center={[6.5244, 3.3792]} 
                                zoom={11} 
                                markers={mapMarkers} 
                            />
                        </div>

                        <div className="filter-bar">
                            <div className="search-mini">
                                <Search size={16} />
                                <input type="text" placeholder="Search hospitals..." />
                            </div>
                            <button className="filter-btn"><Filter size={16} /></button>
                        </div>

                        <div className="list-section">
                            {hospitals.map(hosp => (
                                <div key={hosp.id} className="hosp-card card">
                                    <div className="hosp-info">
                                        <div className="title-row">
                                            <h4>{hosp.name}</h4>
                                            {hosp.is24hr && <span className="tag-24h">24h</span>}
                                        </div>
                                        <p className="area"><MapPin size={12} /> {hosp.area} • {hosp.type}</p>
                                    </div>
                                    <div className="hosp-actions">
                                        <button className="secondary-btn sm">Directions</button>
                                        <button className="primary-btn sm">Call</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'pharmacies' && (
                    <div className="pharmacies-view">
                        <div className="filter-bar">
                            <div className="search-mini">
                                <Search size={16} />
                                <input type="text" placeholder="Search pharmacies..." />
                            </div>
                        </div>
                        {pharmacies.map(pharma => (
                            <div key={pharma.id} className="pharma-card card">
                                <div className="pharma-info">
                                    <h4>{pharma.name}</h4>
                                    <p>{pharma.area}</p>
                                    <div className="tags">
                                        {pharma.is24hr && <span className="tag-green">24h</span>}
                                        {pharma.delivery && <span className="tag-blue">Delivery</span>}
                                    </div>
                                </div>
                                <button className="primary-btn sm">Visit</button>
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === 'telemedicine' && (
                    <div className="telemedicine-view">
                        <div className="tele-header">
                            <h2>Connect with a Doctor</h2>
                            <p>Instant video consultation with verified Nigerian medical professionals.</p>
                        </div>

                        <div className="doctors-list">
                            {doctors.map(doc => (
                                <div key={doc.id} className="doc-card card">
                                    <img src={doc.photo} alt={doc.name} className="doc-img" />
                                    <div className="doc-details">
                                        <h4>{doc.name}</h4>
                                        <p className="specialty">{doc.specialty}</p>
                                        <p className="langs">{doc.languages.join(', ')}</p>
                                        <div className="price-row">
                                            <span className="fee">₦{doc.fee.toLocaleString()}</span>
                                            <button className="primary-btn sm" onClick={() => handleConsultation(doc)}>Consult</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <MockPayment
                isOpen={showPayment}
                onClose={() => setShowPayment(false)}
                amount={selectedDoctor?.fee || 0}
                title="Consultation Fee"
                onSuccess={() => {
                    alert("Success! Dr. " + selectedDoctor.name + " will contact you in 5 minutes.");
                }}
            />

            <style dangerouslySetInnerHTML={{
                __html: `
        .emergency-view { padding-bottom: 20px; }
        .tab-navigation {
          display: flex;
          gap: 10px;
          overflow-x: auto;
          padding-bottom: 12px;
          margin-bottom: 20px;
          scrollbar-width: none;
        }
        .tab-navigation::-webkit-scrollbar { display: none; }
        .tab-navigation button {
          padding: 8px 16px;
          border-radius: 20px;
          background: var(--card-gray);
          color: var(--text-light);
          white-space: nowrap;
          font-weight: 600;
          font-size: 14px;
        }
        .tab-navigation button.active {
          background: var(--primary-green);
          color: white;
        }

        .main-number {
          background: var(--primary-green);
          color: white;
          text-align: center;
          margin-top: 16px;
          margin-bottom: 24px;
          border: none;
        }
        .main-number .number { font-size: 32px; font-weight: 800; display: block; }
        .main-number p { opacity: 0.9; font-size: 14px; margin-bottom: 16px; }

        .contact-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding: 16px;
        }
        .contact-item h4 { font-size: 14px; margin-bottom: 2px; }
        .contact-item .num { font-weight: 700; color: var(--primary-green); }
        .btns { display: flex; gap: 10px; }
        .icon-btn {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--border-gray);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-dark);
        }
        .icon-btn.call { background: var(--light-green); color: var(--primary-green); }

        .map-placeholder {
          height: 200px;
          background: #e5e5e5;
          border-radius: var(--radius-md);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          color: var(--text-light);
        }
        .pins-visual .pin {
          position: absolute;
          width: 10px;
          height: 10px;
          background: var(--primary-green);
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 0 0 10px rgba(0,0,0,0.2);
        }

        .filter-bar {
          display: flex;
          gap: 12px;
          margin-top: 20px;
          margin-bottom: 16px;
        }
        .search-mini {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--card-gray);
          padding: 8px 12px;
          border-radius: 8px;
          border: 1px solid var(--border-gray);
        }
        .search-mini input { border: none; background: none; outline: none; width: 100%; font-size: 14px; }
        .filter-btn {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: var(--card-gray);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hosp-card, .pharma-card { margin-bottom: 12px; }
        .title-row { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
        .tag-24h { font-size: 10px; background: var(--emergency-red); color: white; padding: 2px 6px; border-radius: 4px; }
        .area { font-size: 12px; color: var(--text-light); display: flex; align-items: center; gap: 4px; }
        .hosp-actions { display: flex; gap: 8px; margin-top: 12px; }
        .sm { padding: 6px 12px; font-size: 12px; }

        .pharma-info h4 { margin-bottom: 4px; }
        .pharma-info p { font-size: 12px; color: var(--text-light); }
        .tags { display: flex; gap: 6px; margin-top: 8px; }
        .tag-green { font-size: 10px; background: #d4edda; color: #155724; padding: 2px 6px; border-radius: 4px; }
        .tag-blue { font-size: 10px; background: #cce5ff; color: #004085; padding: 2px 6px; border-radius: 4px; }

        .tele-header { margin-bottom: 24px; }
        .tele-header p { font-size: 14px; color: var(--text-light); margin-top: 4px; }
        .doc-card { display: flex; gap: 16px; margin-bottom: 16px; }
        .doc-img { width: 80px; height: 80px; border-radius: var(--radius-md); object-fit: cover; }
        .doc-details { flex: 1; }
        .specialty { font-size: 13px; color: var(--text-light); font-weight: 500; }
        .langs { font-size: 12px; color: var(--text-light); margin-bottom: 8px; }
        .price-row { display: flex; justify-content: space-between; align-items: center; }
        .fee { font-weight: 700; color: var(--primary-green); }
      `}} />
        </div>
    );
};

export default EmergencyView;
